import { ProjectStore } from "@/stores/project-store.core";
import { TotalsStore } from "@/stores/totals-store.core";
import type { ExtendedGetProjectDetailResponse } from "./types";
import { useQuery } from "@tanstack/react-query";
import type { AlertContext } from "@laborchart-modules/common/dist/rethink/schemas/enums/alerts";

async function fetchProjectDetails(id: string) {
   return (await ProjectStore.getProjectDetails(id).payload) as ExtendedGetProjectDetailResponse;
}

export function useGetProjectQuery(id: string) {
   return useQuery({
      queryKey: ["project", id],
      queryFn: () => fetchProjectDetails(id),
      enabled: !!id,
   });
}

async function fetchProjectTotals(id: string) {
   const data = (
      await TotalsStore.getProjectTotals(id, {
         getAssignments: "true",
         getRequests: "true",
         totalsUnit: "hours",
         viewBy: "project",
      }).payload
   ).data;

   let total_hours = 0;
   const assignment_totals = data.rollup_data.daily_ass_totals;
   const request_totals = data.rollup_data.daily_request_totals;
   Object.keys(assignment_totals).forEach((key: string) => {
      total_hours += Number(assignment_totals[key]);
   });

   Object.keys(request_totals).forEach((key: string) => {
      total_hours += Number(request_totals[key]);
   });

   return total_hours;
}

export function useGetProjectTotalsQuery(id: string) {
   return useQuery({
      queryKey: ["project-totals", id],
      queryFn: () => fetchProjectTotals(id),
      enabled: !!id,
   });
}

export type RawProjectTotals = {
   breakdown_data: [
      {
         id: string;
         name: string;
         daily_request_totals: {
            [key: string]: {
               count: number;
            };
         };
         daily_ass_totals: {
            [key: string]: {
               count: number;
            };
         };
      },
   ];
};

async function fetchRawProjectTotals(id: string) {
   const data = (
      await TotalsStore.getProjectTotals(id, {
         getAssignments: "true",
         getRequests: "true",
         totalsUnit: "people",
         viewBy: "job-title",
      }).payload
   ).data;

   return data as unknown as RawProjectTotals;
}

export function useGetRawProjectTotalsQuery(id: string) {
   return useQuery({
      queryKey: ["raw-project-totals", id],
      queryFn: () => fetchRawProjectTotals(id),
      enabled: !!id,
   });
}

export function useGetProjectAlertInfoQuery(id: string, alertContext: AlertContext) {
   return useQuery({
      queryKey: ["project-alert-info", id, alertContext],
      queryFn: () => ProjectStore.getProjectAlertInfo(id, alertContext).payload,
      enabled: !!id && !!alertContext,
   });
}
